// Helpers for the Friday Conversations rotating venues + themes.
// Maps use a no-API-key Google Maps embed + a "Get Directions" deep link.

const ORDINAL_WORD = { 1: "1st", 2: "2nd", 3: "3rd", 4: "4th", 5: "5th" };

export function ordinalWord(n) {
  return ORDINAL_WORD[n] || `${n}th`;
}

/** Single-line full address built from a location record. */
export function fullAddress(loc) {
  if (!loc) return "";
  const cityState = [loc.city, loc.state].filter(Boolean).join(", ");
  const tail = [cityState, loc.zip_code].filter(Boolean).join(" ");
  return [loc.address, tail].filter(Boolean).join(", ");
}

/** Map search query — venue name + address improves pin accuracy. */
function mapQuery(loc) {
  return [loc?.name, fullAddress(loc)].filter(Boolean).join(", ");
}

/** No-key embeddable Google map for an iframe `src`. */
export function mapEmbedSrc(loc) {
  const q = encodeURIComponent(mapQuery(loc));
  return `https://maps.google.com/maps?q=${q}&z=15&output=embed`;
}

/** Deep link that opens turn-by-turn directions in the user's maps app. */
export function directionsUrl(loc) {
  const q = encodeURIComponent(mapQuery(loc));
  return `https://www.google.com/maps/dir/?api=1&destination=${q}`;
}

/**
 * Theme-aware accent for a Friday (Blue or Copper only).
 * Uses --accent (blue) / --primary (copper) so contrast holds in all 4 themes.
 * Returns an inline style object defining the --fc-friday-accent variable.
 */
export function accentStyle(accent) {
  const value = accent === "copper" ? "hsl(var(--primary))" : "hsl(var(--accent))";
  return { "--fc-friday-accent": value };
}

/**
 * Distinct accent per Friday-of-month for the "Friday Rhythm" cards so all four
 * boxes read as four different colors: 1 → blue, 2 → copper, 3 → green, 4 → purple.
 * Uses themed CSS tokens so contrast holds across all themes + light/dark.
 */
export function rhythmAccentStyle(ordinal) {
  const MAP = {
    1: "hsl(var(--accent))",
    2: "hsl(var(--primary))",
    3: "hsl(var(--fc-rhythm-green))",
    4: "hsl(var(--fc-rhythm-purple))",
  };
  return { "--fc-friday-accent": MAP[ordinal] || "hsl(var(--accent))" };
}

/**
 * Which Friday-of-month a given ISO date (YYYY-MM-DD) falls on.
 * Returns { isFriday, ordinal } where ordinal is the Nth occurrence (1–5).
 */
export function fridayInfo(dateStr) {
  if (!dateStr) return null;
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const ordinal = Math.floor((d.getDate() - 1) / 7) + 1;
  return { isFriday: d.getDay() === 5, ordinal };
}

/**
 * Current wall-clock in US Pacific time (America/Los_Angeles), as { date, time }
 * strings ("YYYY-MM-DD", "HH:MM", 24h). Auto-handles PST/PDT. We compare Pacific
 * wall-clock to Pacific wall-clock so the result is correct regardless of the
 * viewer's own timezone.
 */
export function pacificNowParts(now = new Date()) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Los_Angeles",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(now);
  const get = (t) => (parts.find((p) => p.type === t) || {}).value;
  let hh = get("hour");
  if (hh === "24") hh = "00"; // some engines emit 24 at midnight
  return { date: `${get("year")}-${get("month")}-${get("day")}`, time: `${hh}:${get("minute")}` };
}

/**
 * True once an event has finished, in US Pacific time. Uses the event's own
 * end_time ("HH:MM"), then its venue's end_time (e.g. "20:00" for a 5–8 PM Friday),
 * and finally 15:00 (3 PM) when neither is set. Events without a date are treated as
 * not-ended.
 */
export function eventHasEnded(ev, now = new Date()) {
  if (!ev || !ev.date) return false;
  const own = typeof ev.end_time === "string" ? ev.end_time.trim() : "";
  const venue = typeof ev.location?.end_time === "string" ? ev.location.end_time.trim() : "";
  const raw = own || venue;
  const end = /^\d{1,2}:\d{2}/.test(raw) ? raw.slice(0, 5).padStart(5, "0") : "15:00";
  const { date: nowDate, time: nowTime } = pacificNowParts(now);
  if (ev.date < nowDate) return true;
  if (ev.date > nowDate) return false;
  return nowTime >= end; // same Pacific day: ended once now passes end_time
}

export const DEFAULT_START = "12:00";
export const DEFAULT_END = "15:00";

/** A venue's Friday hours ("HH:MM" Pacific), falling back to the noon–3 default. */
export function locTimes(loc) {
  return { start: loc?.start_time || DEFAULT_START, end: loc?.end_time || DEFAULT_END };
}

function clockPart(t, withSuffix) {
  const [h, m] = t.split(":").map(Number);
  const h12 = h % 12 || 12;
  const body = m ? `${h12}:${String(m).padStart(2, "0")}` : `${h12}`;
  return withSuffix ? `${body} ${h < 12 ? "AM" : "PM"}` : body;
}

/** Compact range like "12–3 PM" or "5–8 PM" (suffix repeated only when AM/PM differ). */
export function timeRange(start = DEFAULT_START, end = DEFAULT_END) {
  const sameHalf = (Number(start.split(":")[0]) < 12) === (Number(end.split(":")[0]) < 12);
  return `${clockPart(start, !sameHalf)}–${clockPart(end, true)}`;
}

/** Venues whose hours differ from the noon–3 default, e.g. a 5 PM start. */
export function scheduleExceptions(locations = []) {
  return locations
    .filter((l) => l.friday_ordinal && (l.start_time || l.end_time) && (locTimes(l).start !== DEFAULT_START || locTimes(l).end !== DEFAULT_END))
    .sort((a, b) => a.friday_ordinal - b.friday_ordinal);
}

/** "12–3 PM (3rd Friday 5–8 PM)" for headers and badges. */
export function hoursSummary(locations = []) {
  const ex = scheduleExceptions(locations);
  if (ex.length === 0) return timeRange();
  return `${timeRange()} (${ex.map((l) => `${ordinalWord(l.friday_ordinal)} Friday ${timeRange(locTimes(l).start, locTimes(l).end)}`).join(", ")})`;
}

/** "noon", "1 PM", "5:30 PM" — for prose. */
export function clockWord(t) {
  const [h, m] = t.split(":").map(Number);
  if (h === 12 && m === 0) return "noon";
  return clockPart(t, true);
}

/** Shift "HH:MM" by whole hours. */
export function shiftHours(t, hours) {
  const [h, m] = t.split(":").map(Number);
  return `${String((h + hours + 24) % 24).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}
